/**
 * Dependencies.
 */
import { debounce, deburr, includes, toLower } from 'lodash-es';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';

import { ServicePointCollectModel } from '@/api/service-point/collect/collect.model';
import { servicePointOrderService } from '@/api/service-point/order/order.service';
import { servicePointService } from '@/api/service-point/service-point/service-point.service';
import orderState from '@/modules/service-point/constants/order.constant';
import { servicePointStore } from '@/modules/service-point/store';

/**
 * Component.
 */
@Component({
  name: 'collect-list-scene'
})
export default class extends Vue {
  /**
   * Property `isLoadingData`.
   */
  isLoadingData = false;

  /**
   * Property `isCollecting`.
   */
  isCollecting = false;

  /**
   * Property `isCollectModalVisible`.
   */
  isCollectModalVisible = false;

  /**
   * Property `trackingNumberSelected`.
   */
  trackingNumberSelected = null;

  /**
   * Property `collects`.
   */
  collects: (ServicePointCollectModel & {
    isCollected?: boolean;
  })[] = [];

  /**
   * Property `collectsFiltered`.
   */
  collectsFiltered: (ServicePointCollectModel & {
    isCollected?: boolean;
  })[] = [];

  /**
   * Property `filter`.
   */
  filter: {
    key: string;
    value: string;
  } = {
    key: 'client.name',
    value: null
  };

  /**
   * Property `filterItems`.
   */
  filterItems = [
    {
      id: 'client.name',
      name: this.i18n('SERVICE_POINT.LABELS.CLIENT_NAME')
    },
    {
      id: 'orderCode',
      name: this.i18n('SERVICE_POINT.LABELS.ORDER_CODE')
    },
    {
      id: 'trackingNumber',
      name: this.i18n('SERVICE_POINT.LABELS.TRACKING_NUMBER')
    }
  ];

  /**
   * Property `searchFilter`.
   */
  searchFilter = debounce(this.handleSearchCollects, 1000);

  /**
   * Getter `currentTrackingNumberKey`.
   */
  get currentTrackingNumberKey() {
    const trackingNumberKey = servicePointStore.deliverTrackingNumberKey;

    return trackingNumberKey;
  }

  /**
   * Lifecycle created hook.
   */
  created() {
    this.handleGetCollects();
  }

  /**
   * Handle get collects.
   */
  async handleGetCollects() {
    try {
      this.isLoadingData = true;

      const response =
        (await servicePointService.getOrders({
          status: orderState.RECEIVE_PENDING_TO_COLLECT
        })) || [];

      response.forEach(item => {
        const { delivery, statusDate: expectedCollectDate } = item || {};
        const { client, orderCode, trackingNumber } = delivery || {};
        const { name } = client || {};

        this.collects = [
          ...this.collects,
          {
            client: { name },
            expectedCollectDate,
            orderCode,
            trackingNumber
          }
        ];
      });
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleGetCollects' });
      }
    } finally {
      this.isLoadingData = false;
    }

    this.collectsFiltered = [...this.collects];
  }

  /**
   * Handle search collects.
   */
  handleSearchCollects() {
    const { collects = [], filter } = this || {};

    this.isLoadingData = true;

    let collectsFiltered = [...collects];

    if (filter.value) {
      collectsFiltered = collects.filter(item => {
        const value = (filter.key || '')
          .split('.')
          .reduce((obj, index) => obj[index], item);

        return includes(deburr(toLower(value)), deburr(toLower(filter.value)));
      });
    }
    this.isLoadingData = false;

    this.collectsFiltered = [...collectsFiltered];
  }

  /**
   * Handle collect.
   */
  async handleCollect({ trackingNumber }) {
    const collect = this.collects.find(
      ({ trackingNumber: tracking }) => tracking === trackingNumber
    );

    this.isCollecting = true;

    try {
      await servicePointOrderService.markHasCollected({
        trackingNumber
      });

      collect.isCollected = true;
    } catch (error) {
      collect.isCollected = false;

      this.$kit.notify.error(
        this.i18n('SERVICE_POINT.LABELS.ERROR'),
        this.i18n('SERVICE_POINT.ERRORS.SOMETHING_WENT_WRONG')
      );

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({ error, method: 'handleCollect' });
      }
    } finally {
      this.collectsFiltered = [...this.collectsFiltered];
      this.isCollecting = false;
    }
  }

  /**
   * Handle go back.
   */
  handleGoBack() {
    this.$router.back();
  }

  /**
   * Handle open collect modal.
   */
  handleOpenCollectModal({ trackingNumber }) {
    this.trackingNumberSelected = trackingNumber;
    this.isCollectModalVisible = true;
  }

  /**
   * Handle cancel collect.
   */
  handleCancelCollect() {
    this.trackingNumberSelected = null;
    this.isCollectModalVisible = false;
  }

  /**
   * Handle confirm collect.
   */
  async handleConfirmCollect() {
    await this.handleCollect({ trackingNumber: this.trackingNumberSelected });
    this.trackingNumberSelected = null;
    this.isCollectModalVisible = false;
  }

  /**
   * Hanlde filter key change.
   */
  @Watch('filter.key')
  handleFilterKeyChange() {
    this.handleSearchCollects();
  }
}
